import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Close as CloseIcon } from '@material-ui/icons';
import api from '~/services';
import { log, integrationConfig } from '~/helpers';
import * as lib from '~/dependencies';
import { DialogTitle, CustomBtn } from './styles';

const {
  AUTO_TASK,
  ONE_PAGE_CRM,
  SALESFORCE,
  BULLHORN,
  ZOHO_ID,
  GO_HIGH_LEVEL_ID,
  HUBSPOT_ID,
  ACTIVE_CAMPAIGN_ID,
} = integrationConfig;

const integrations = {
  18: 'Zendesk',
  22: 'Salesforce',
  30: 'Pipedrive',
  34: 'HubSpot',
  42: 'Zoho',
  46: 'ActiveCampaign',
  56: 'AutoTask',
  58: 'One Page CRM',
  60: 'Keap',
  63: 'Bullhorn',
  65: 'GoHighLevel',
  57: 'Odoo',
};

const MINIMUM_CHARACTERS_TO_DEBOUNCE = 3;

/**
 * Check if the last name field is required for the current integration
 * @param {number} integrationId
 */
const isLastNameRequired = integrationId => {
  return (
    integrationId === AUTO_TASK ||
    integrationId === ONE_PAGE_CRM ||
    integrationId === ZOHO_ID ||
    integrationId === HUBSPOT_ID ||
    integrationId === ACTIVE_CAMPAIGN_ID ||
    String(integrationId).startsWith(GO_HIGH_LEVEL_ID)
  );
};

export default function ContactFormModal({
  handleClose,
  handleSubmit,
  integrationId,
}) {
  // STATES
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(0);
  const [noOptionsText, setNoOptionsText] = useState(
    'Type 3 more characters to search.'
  );
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [leads, setLeads] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [contactType, setContactType] = useState(null);
  const [observation, setObservation] = useState();
  const [salesforceCompany, setSalesforceCompany] = useState('');
  const [isFetchingCompanies, setIsFetchingCompanies] = useState(false);

  const connectedIntegrations = useSelector(
    state => state.integrations?.accountIntegrations
  );

  const currentIntegrationInfo = connectedIntegrations?.find(
    e => Number(e.id) === integrationId
  );

  const allowedTypes = currentIntegrationInfo?.contactFields.find(
    field => field.name === 'type'
  )?.values;

  // HOOKS
  const { t } = lib.useTranslation();

  /**
   * Retriave companies from auto_task/companies?name={<companyName>}
   * @param {string} companyName
   * @description AUTO TASK ONLY
   */
  const getCompanies = lib._.debounce(async (companyName, isCompanyLeads) => {
    const source = lib.axios.CancelToken.source();
    try {
      setIsFetchingCompanies(true);

      const companiesList = await api.integrations.get.getAutoTaskCompanyByName(
        companyName,
        isCompanyLeads,
        {
          cancelToken: source.token,
        }
      );

      setCompanyId(0);

      const { data } = companiesList;
      setCompanies(data);

      if (data?.length === 0) {
        setNoOptionsText('Company name not found.');
      }

      setIsFetchingCompanies(false);
    } catch (err) {
      log.error('Failure to retrive the AutoTask companies. Reason: ', err);
    }
  }, 500);

  /**
   * Retriave companies by name when user input company name longer then 3 characters
   * @param {string} companyName
   * @description AUTO TASK ONLY
   */
  const handleOnTypingCompanyName = (event, companyName) => {
    if (companyName?.length < MINIMUM_CHARACTERS_TO_DEBOUNCE) {
      setCompanies([]);
      setNoOptionsText(
        `Type ${MINIMUM_CHARACTERS_TO_DEBOUNCE -
          companyName.length} more characters to search`
      );
    }

    if (companyName?.length >= MINIMUM_CHARACTERS_TO_DEBOUNCE) {
      getCompanies(companyName, leads);
    }
  };

  /**
   * Set the selected companyId once the user choose an option
   * @param {number} optionIndex
   * @description AUTO TASK ONLY
   */
  const onChooseCompanyOption = event => {
    const currentCompanyOption = companies[event.target.dataset.optionIndex];
    setCompanyId(currentCompanyOption.external_id);
  };

  function handleFormSubmit() {
    if (integrationId === ZOHO_ID) {
      if (
        lib._.isEmpty(name) ||
        lib._.isEmpty(lastName) ||
        lib._.isEmpty(contactType)
      ) {
        setIsRequired(true);
        return;
      }
    }

    if (lib._.isEmpty(name)) {
      setIsRequired(true);
      return;
    }

    switch (integrationId) {
      case AUTO_TASK:
        if (!lib._.isNull(companyId)) {
          handleSubmit(companyId, name, lastName);
        }
        break;
      case SALESFORCE:
        if (contactType === 'lead' && salesforceCompany.length < 1) {
          setIsRequired(true);
          return;
        }

        handleSubmit(
          null,
          name,
          lastName,
          contactType,
          observation,
          salesforceCompany
        );
        break;
      case BULLHORN:
        handleSubmit(
          null,
          name,
          lastName,
          contactType,
          observation,
          salesforceCompany
        );
        break;
      default:
        handleSubmit(null, name, lastName, contactType, observation);
        break;
    }
  }

  return (
    <Dialog
      open
      aria-labelledby="contact-form-modal-title"
      fullWidth
      maxWidth="md">
      <DialogTitle id="contact-form-modal-title" disableTypography>
        <span style={{ marginLeft: 10 }}>
          {t('components.integrations.integrationDialog.saveNewContact')}
        </span>
        <IconButton
          aria-label="close"
          style={{ padding: 3 }}
          onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {t('components.integrations.integrationDialog.subTitle', {
            integrationName: integrations[parseInt(integrationId, 10)],
          })}
        </DialogContentText>
        <TextField
          id="input-integrated-contact-first-name"
          label={
            isLastNameRequired(integrationId)
              ? t('components.integrations.integrationDialog.fieldName')
              : t('components.integrations.integrationDialog.fieldContactName')
          }
          margin="dense"
          value={name}
          onChange={e => setName(e.target.value)}
          error={isRequired && name.length < 1}
          required
          fullWidth
        />
        {isLastNameRequired(integrationId) && (
          <>
            <TextField
              id="input-integrated-contact-last-name"
              label={t('components.integrations.integrationDialog.lastName')}
              margin="dense"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              error={isRequired && lastName.length < 1}
              fullWidth
              required
            />
            {integrationId === AUTO_TASK && (
              <FormControl fullWidth margin="dense">
                <FormControlLabel
                  style={{ marginBottom: 10 }}
                  control={
                    <Checkbox
                      id="checkbox-include-company-leads"
                      color="primary"
                      checked={leads}
                      onChange={e => setLeads(e.target.checked)}
                    />
                  }
                  label="Include company leads"
                />
                <Autocomplete
                  id="autocomplete-search-for-company-name"
                  fullWidth
                  loadingText="Loading..."
                  loading={isFetchingCompanies}
                  clearOnEscape
                  options={companies}
                  getOptionLabel={option => option.name}
                  noOptionsText={noOptionsText}
                  renderInput={params => (
                    <TextField
                      variant="outlined"
                      label={t(
                        'components.integrations.integrationDialog.labelCompany'
                      )}
                      {...params}
                    />
                  )}
                  onInputChange={handleOnTypingCompanyName}
                  onChange={onChooseCompanyOption}
                />
              </FormControl>
            )}
          </>
        )}
        {integrationId === SALESFORCE && (
          <FormControl fullWidth margin="dense">
            <InputLabel id="select-label-salesforce" required>
              {t('components.integrations.integrationDialog.type.title')}
            </InputLabel>
            <Select
              labelId="select-label-salesforce"
              id="simple-select-salesforce"
              value={contactType}
              onChange={ev => setContactType(ev.target.value)}
              onLoad={() => {
                if (!contactType) setContactType('contact');
              }}
              required>
              <MenuItem key="contact" value="contact">
                {t('components.integrations.integrationDialog.type.contact')}
              </MenuItem>
              <MenuItem key="lead" value="lead">
                {t('components.integrations.integrationDialog.type.lead')}
              </MenuItem>
            </Select>
            {contactType === 'lead' && (
              <TextField
                id="salesforce-company"
                label={t(
                  'components.integrations.integrationDialog.labelCompany'
                )}
                margin="dense"
                value={salesforceCompany}
                onChange={e => setSalesforceCompany(e.target.value)}
                error={isRequired && salesforceCompany.length < 1}
                fullWidth
                required
              />
            )}
            <TextField
              id="salesforce-obs"
              label={t('components.integrations.integrationDialog.observation')}
              margin="dense"
              value={observation}
              onChange={e => setObservation(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
          </FormControl>
        )}
        {integrationId === BULLHORN && (
          <FormControl fullWidth margin="dense">
            <InputLabel id="first-name-bullhorn" required>
              {t('components.integrations.integrationDialog.type.title')}
            </InputLabel>
            <Select
              onLoad={() => {
                if (!contactType) setContactType('candidate');
              }}
              labelId="select-type-bullhorn"
              id="select-type-bullhorn"
              value={contactType}
              onChange={ev => setContactType(ev.target.value)}
              required>
              {/* TODO: Display options from active integratoins, contact_fields->type->values */}
              <MenuItem key="candidate" value="candidate">
                Candidate
              </MenuItem>
              <MenuItem key="lead" value="lead">
                {t('components.integrations.integrationDialog.type.lead')}
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {integrationId === ZOHO_ID && (
          <FormControl fullWidth margin="dense">
            <InputLabel id="first-name-zoho" required>
              {t('components.integrations.integrationDialog.type.title')}
            </InputLabel>
            <Select
              onLoad={() => {
                if (!contactType) setContactType('contact');
              }}
              labelId="select-type-zoho"
              id="select-type-zoho"
              value={contactType}
              onChange={ev => setContactType(ev.target.value)}
              error={isRequired && contactType === null}>
              <MenuItem key="contact" value="contact">
                Contact
              </MenuItem>
              <MenuItem key="lead" value="lead">
                {t('components.integrations.integrationDialog.type.lead')}
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {String(integrationId).startsWith(GO_HIGH_LEVEL_ID) && (
          <FormControl fullWidth margin="dense">
            <InputLabel id="first-name-go-high-level" required>
              {t('components.integrations.integrationDialog.type.title')}
            </InputLabel>
            <Select
              onLoad={() => {
                if (!contactType) setContactType('contact');
              }}
              labelId="select-type-go-high-level"
              id="select-type-go-high-level"
              value={contactType}
              onChange={ev => setContactType(ev.target.value)}
              error={isRequired && contactType === null}>
              {allowedTypes &&
                allowedTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type && type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        <DialogActions>
          <CustomBtn onClick={() => handleFormSubmit()}>
            {t('components.integrations.integrationDialog.btnSave')}
          </CustomBtn>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

ContactFormModal.propTypes = {
  handleClose: lib.PropTypes.func.isRequired,
  handleSubmit: lib.PropTypes.func.isRequired,
  integrationId: lib.PropTypes.number.isRequired,
};
